import React from 'react';

import {useSelector} from 'react-redux';

import {CreateDocumentButton} from 'app/components/general/CreateDocumentButton';
import {DashboardPage} from 'app/components/general/DashboardPage';
import {DashboardSidebarFoldersList} from 'app/components/general/DashboardPage/DashboardSidebarFoldersList';
import {DashboardSidebarPoliciesList} from 'app/components/general/DashboardPage/DashboardSidebarPoliciesList';
import {DashboardSidebarProcessesList} from 'app/components/general/DashboardPage/DashboardSidebarProcessesList';
import {DashboardSidebarTagsList} from 'app/components/general/DashboardPage/DashboardSidebarTagsList';
import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {DocumentBulkActionsButton} from 'app/components/general/DocumentBulkActionsButton';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {FolderLink} from 'app/folders/components/FolderLink';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {useProcedures} from 'app/procedures/api';
import {CompactProcedureListItem} from 'app/procedures/components/CompactProcedureListItem';
import {useProcedureFilters} from 'app/procedures/hooks';
import {toggleDashboardRelation} from 'app/procedures/slice';
import {PROCEDURES_ADD_NEW, PROCEDURES_HOME} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {
  ProceduresFetchParamsType,
  ProceduresLocationParamsType,
} from 'app/procedures/types';
import type {WithRouterProps} from 'react-router';
import type {ContentType, ProcedureSerializedType} from 'sweet-types';

export const ProceduresDashboard = (
  routerProps: WithRouterProps<unknown, ProceduresLocationParamsType>
) => {
  const {isTeamMember} = getCurrentUserPerms(useCurrentUserState().data!);
  const currentUser = useCurrentUserState().data!;

  const actions = useAsyncActions({toggleDashboardRelation});
  const activeRelations = useSelector((state) => state.procedures.relations);

  return (
    <DashboardPage<
      ProcedureSerializedType,
      ProceduresLocationParamsType,
      ProceduresFetchParamsType
    >
      {...routerProps}
      actionArea={
        <CreateDocumentButton
          contentType="procedure"
          disabled={!isTeamMember}
        />
      }
      sidebar={
        <>
          <DashboardSidebarFoldersList />
          <DashboardSidebarProcessesList canCreate={isTeamMember} />
          <DashboardSidebarPoliciesList canCreate={isTeamMember} />
          <DashboardSidebarTagsList />
        </>
      }
      header={({resetFilters, params, count}) =>
        params.folder != null ? (
          <div className="my-2 text-center">
            <h4>
              <i className="far fa-folder-open" /> {gt('Folder contents')}
              <span>
                <FolderLink hashid={params.folder} className="small ml-3" />
              </span>
            </h4>
            {count != 0 ? (
              <button
                className="btn-outline-secondary btn btn-sm"
                onClick={resetFilters}
              >
                <i className="fa fa-times" />{' '}
                {gt('Reset filters and view all procedures.')}
              </button>
            ) : null}
          </div>
        ) : null
      }
      useDataHook={useProcedures}
      useFiltersHook={useProcedureFilters}
      contentType={'procedure'}
      enableNotifications={true}
      enableBulkSelect={currentUser.is_manager}
      filterbar={({resetFilters, viewingAll}) => (
        <Filterbar
          ct="procedure"
          resetFilters={resetFilters}
          viewingAll={viewingAll}
          show={{
            team: true,
            tags: true,
            sort: true,
            filters: true,
            search: true,
          }}
        />
      )}
      renderItem={(
        procedure,
        {params, refetch},
        isSelected,
        addToSelection,
        removeFromSelection,
        notifications
      ) => (
        <CompactProcedureListItem
          key={procedure.id}
          object={procedure}
          refreshData={refetch}
          ordering={params.ordering}
          notifications={notifications}
          activeRelations={activeRelations}
          showRelations={true}
          toggleProceduresDashboardRelation={(
            contentType: ContentType,
            isOpen: boolean
          ) =>
            actions.toggleDashboardRelation({
              contentType,
              isOpen,
            })
          }
          enableBulkSelect={currentUser.is_manager}
          isSelected={isSelected}
          addToSelection={addToSelection}
          removeFromSelection={removeFromSelection}
        />
      )}
      onModalChangeMessage={({refetch}) =>
        (message, {runWhenAnyModelMatch}) => {
          runWhenAnyModelMatch(message, ['procedure', 'procedureversion'], () =>
            refetch()
          );
        }}
      messages={{
        counter: (count) => ngt('%s procedure', '%s procedures', count),
        topListActions: (selectedDocsOnThisPage, {refetch}) => (
          <DocumentBulkActionsButton
            documents={selectedDocsOnThisPage.map((selectedDoc) => ({
              document_content_type: selectedDoc.content_type,
              document: selectedDoc,
              url: selectedDoc.url,
              can_edit: selectedDoc.can_edit,
              can_delete: selectedDoc.can_delete,
              object_id: selectedDoc.id,
            }))}
            refreshData={refetch}
            enableBulkSelect={currentUser.is_manager}
          />
        ),
        notFound: ({viewingAll, resetFilters}, NoResultsMessage) =>
          viewingAll ? (
            <NoResultsMessage
              icon="fa-cogs"
              head={gt('No procedures on your account')}
            >
              {isTeamMember ? (
                <FullHTMLLink
                  className="btn btn-primary"
                  html_url={PROCEDURES_ADD_NEW}
                >
                  {gt('Create a new procedure')}
                </FullHTMLLink>
              ) : null}
            </NoResultsMessage>
          ) : (
            <NoResultsMessage
              icon="fa-cogs"
              head={gt('No procedures found with these filters')}
            >
              <button
                className="btn-outline-secondary btn btn-sm"
                onClick={resetFilters}
              >
                <i className="fa fa-times" />{' '}
                {gt('Reset filters and view all procedures')}
              </button>
            </NoResultsMessage>
          ),
      }}
      subscribeToPresence={{
        contentType: 'procedure',
        objectsIds: (procedures) => procedures.map((procedure) => procedure.id),
      }}
    />
  );
};

const procedureCt = getContentType('procedure');

export const ProceduresDashboardBreadcrumb = (props: BreadcrumbProps) => (
  <BreadcrumbItemStructure
    {...props}
    icon={procedureCt.icon}
    text={gt('Procedures')}
    link={PROCEDURES_HOME}
  />
);
